import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigationDetector } from '~/hooks';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';
import { isObjectEqual } from '~/utils/Object';
import {
  fetchAverageRating,
  getUserEducation,
  getUserOvertimeLimits,
  selectDaysMDB,
} from '../../../../actions';
import { UserProfession, UserSubProfession } from '../../../../enums/UserProfession';
import {
  getShouldOfficeShowEfda,
  getShouldUseParentPostShiftForChild,
} from '../../../../growthbook';
import history from '../../../../browserHistory';
import { useUserSelector } from '../../../../hooks/useUserSelector';
import { getAllProfessionTypeOptions } from '../../../../utils/DentistUtils';
import { EnumHelper } from '../../../../utils/EnumHelper';

export const useShiftDetailsStep = (job) => {
  const dispatch = useDispatch();
  const selectedDate = history.location?.state?.selectedDate;
  const professionals = history?.location?.state?.professionals;
  const officeId = history?.location?.state?.officeId;
  const professionTypesNames = history?.location?.state?.professionTypesNames;
  const [confirmationExitModalOpen, setConfirmationExitModalOpen] = useState(false);
  const user = useUserSelector();
  const jobInfo = useSelector((state) => state.multiday.jobInfo);
  const officeCredentials = useSelector((state) => state.userCredentials.credentials);
  const jobDates = useSelector((state) => state.multiday.jobDates);

  const getInitialValues = useCallback(() => {
    const newFormValues = {
      officeId: officeId || '',
      offerStartTime: job?.localStart || null,
      offerEndTime: job?.localEnd || null,
      rate: job?.rate || 0,
      lunch: job?.break_time === 0 ? '0 min' : job?.break_time || null,
      isLunchPaid: job?.is_break_paid || false,
      professionType:
        professionTypesNames?.[0] ??
        UserSubProfession.getName(job?.subProfession) ??
        UserProfession.getName(job?.profession) ??
        'Dental Hygienist',
      skills: job?.specialty || 'specialty_General Dentistry',
      procedures: job?.procedures ?? [],
      enableAutoFill: job?.autoFill || false,
      enableAutoFillFavorites: job?.autoFillFavorites || false,
      isAssistedHygiene: job?.isAssistedHygiene || user.office_info.isAssistedHygiene,
      professionals,
    };

    return newFormValues;
  }, [job, professionals, user.office_info.isAssistedHygiene, professionTypesNames]);
  const [formValues, setFormValues] = useState(getInitialValues());
  const fetchAverageRatingRef = useRef(true);

  const { allowNavigation, blockNavigation, clearPendingNavigation, navigateToPending } =
    useNavigationDetector({
      when:
        !isObjectEqual(formValues, getInitialValues()) ||
        jobDates?.length !== 0 ||
        formValues.professionals?.length !== 0 ||
        formValues.professionType !== 'Dental Hygienist',
      onNavigationAttempt: () => {
        setConfirmationExitModalOpen(true);
        blockNavigation();
      },
    });

  const educationInfo = officeCredentials?.education;

  // TODO(lucky): use specialty from userCredentials
  const defaultSpecialty =
    Object.keys(educationInfo?.specialtiesAndProcedures || {})[0] || 'specialty_General Dentistry';

  const selectedProfessionCode = useSelector((state) => {
    const { allProfessions } = state.user;
    const showEfda = getShouldOfficeShowEfda();
    const allProfessionOptions = getAllProfessionTypeOptions(allProfessions, { showEfda });

    return allProfessionOptions?.filter((item) => item.name === formValues.professionType)?.[0]
      ?.code;
  });
  const isParentOffice = useIsParentOfficeSelector();

  const isOfficeFieldValid =
    getShouldUseParentPostShiftForChild() && isParentOffice ? !!formValues.officeId : true;

  const isDisabled =
    !isOfficeFieldValid ||
    !formValues.offerStartTime ||
    !formValues.offerEndTime ||
    formValues.rate === 0 ||
    !formValues.lunch ||
    (selectedProfessionCode === UserProfession.RDH && formValues.isAssistedHygiene === undefined);

  const jobData = {
    officeId: formValues.officeId,
    localStart: formValues.offerStartTime,
    localEnd: formValues.offerEndTime,
    rate: formValues.rate,
    is_break_paid: formValues.isLunchPaid,
    break_time: formValues.lunch,
    profession: UserProfession.getNormalizedCode(selectedProfessionCode),
    subProfession: EnumHelper.getObjectKeys(UserSubProfession).includes(selectedProfessionCode)
      ? selectedProfessionCode
      : null,
    specialty: formValues.skills || 'specialty_General Dentistry',
    autoFill: formValues.enableAutoFill,
    autoFillFavorites: formValues.enableAutoFillFavorites,
    procedures: formValues.procedures,
    ...(selectedProfessionCode === UserProfession.RDH && {
      isAssistedHygiene: formValues.isAssistedHygiene,
    }),
    professionals: formValues.professionals,
  };

  useEffect(() => {
    setFormValues((prevState) => ({
      ...prevState,
      skills: defaultSpecialty,
      procedures: [],
      enableAutoFill: false,
      enableAutoFillFavorites: false,
    }));

    const professionCode = UserProfession.getNormalizedCode(
      UserProfession.getCode(formValues.professionType),
    );

    dispatch(getUserEducation({ profession: professionCode }));
  }, [formValues.professionType]);

  useEffect(() => {
    setFormValues((prevState) => ({ ...prevState, procedures: [] }));
  }, [formValues.skills]);

  /*
    Add date selected in Calendar screen or erase the date state
  */
  useEffect(() => {
    if (!jobInfo?.length) {
      dispatch(
        selectDaysMDB({ jobData: selectedDate ? { [selectedDate]: new Date(selectedDate) } : [] }),
      );
    }
  }, [jobInfo, selectedDate, dispatch]);

  useEffect(() => {
    if (fetchAverageRatingRef.current) {
      fetchAverageRatingRef.current = false;
      dispatch(fetchAverageRating());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!user?.overtimeLimits) {
      dispatch(getUserOvertimeLimits());
    }
  }, [dispatch, user?.overtimeLimits]);

  return {
    formValues,
    setFormValues,
    isDisabled,
    jobData,
    allowNavigation,
    confirmationExitModalOpen,
    setConfirmationExitModalOpen,
    clearPendingNavigation,
    navigateToPending,
  };
};
